import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {loadOmeZarr, getChannelStats} from "@hms-dbmi/viv";
import {loadSample, saveWindow, dataUrl, type MarkerWindow} from "./api";
import {hexToRgb} from "./wm";
import {canonicalColor} from "./palette";

// Lab-canonical calibration gate. When a marker has NO canonical window yet,
// picking on that marker is blocked until a reviewer tunes ONE lo/hi/color and
// approves it against ≥4 samples at once. The window is global (shared via
// POST /windows), so this is a one-time setup per marker — once approved,
// everyone inherits it and the gate never shows again.

interface Preview {
  sid: string;
  condition: string;
  w: number;
  h: number;
  marker: ArrayLike<number>;
  hoechst: ArrayLike<number> | null;
}

const N_SAMPLES = 4;
const HOECHST_LO = 100, HOECHST_HI = 8000, HOECHST_DIM = 0.42;

function clip01(v: number) { return v < 0 ? 0 : v > 1 ? 1 : v; }

// Balance the panel across conditions so a single window is judged on the full
// range of tissue, not four look-alike samples. Shuffle-seeded so the reviewer
// can re-roll for more evidence.
function chooseSamples(
  roster: Array<{sid: string; condition: string}>, seed: number,
): string[] {
  const byCond = new Map<string, string[]>();
  for (const r of roster) {
    const k = r.condition || "?";
    (byCond.get(k) || byCond.set(k, []).get(k)!).push(r.sid);
  }
  // Deterministic shuffle per bucket using the seed.
  const shuffled = [...byCond.values()].map(list => {
    const a = list.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = (Math.sin(seed * 999 + i) * 10000) % (i + 1);
      const k = Math.abs(Math.floor(j));
      [a[i], a[k]] = [a[k], a[i]];
    }
    return a;
  });
  // Round-robin across conditions so we get a mix (2 CAD + 2 CTL when possible).
  const out: string[] = [];
  let idx = 0;
  while (out.length < N_SAMPLES && shuffled.some(l => l.length > idx)) {
    for (const l of shuffled) {
      if (l.length > idx) out.push(l[idx]);
      if (out.length >= N_SAMPLES) break;
    }
    idx++;
  }
  return out.slice(0, N_SAMPLES);
}

export function CalibrationGate({
  marker, roster, reviewerName, onApproved,
}: {
  marker: string;
  roster: Array<{sid: string; donor_id: string; condition: string}>;
  reviewerName: string;
  onApproved: (w: MarkerWindow) => void;
}) {
  const [seed, setSeed] = useState(1);
  const chosen = useMemo(() => chooseSamples(roster, seed), [roster, seed]);
  const [previews, setPreviews] = useState<Preview[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("Loading previews…");
  const [lo, setLo] = useState(100);
  const [hi, setHi] = useState(5000);
  const [color, setColor] = useState("#FFFFFF");
  const [sliderMax, setSliderMax] = useState(20000);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);

  // Load the 4 sample previews (lowest pyramid level only — small + cheap).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setConfirmed(false);
    (async () => {
      const out: Preview[] = [];
      let seededColor = "";
      const los: number[] = [], his: number[] = [];
      let dataMax = 0;
      for (const sid of chosen) {
        if (cancelled) return;
        try {
          const s = await loadSample(sid);
          const mIdx = s.channels.findIndex(c => c.label === marker);
          if (mIdx < 0) continue;
          // Prefer the canonical palette colour so an approved window carries
          // the same colour the export renders; fall back to the manifest colour.
          if (!seededColor) seededColor = canonicalColor(marker) || s.channels[mIdx].color;
          const hIdx = s.channels.findIndex(c => c.label === "Hoechst");
          const ldr = await loadOmeZarr(dataUrl(s.zarr_url), {type: "multiscales"} as any);
          const lowest = ldr.data[ldr.data.length - 1];
          const [, h, w] = lowest.shape;
          const mRaster = await lowest.getRaster({selection: {c: mIdx}});
          let hRaster: any = null;
          if (hIdx >= 0) hRaster = await lowest.getRaster({selection: {c: hIdx}});
          out.push({sid, condition: s.condition, w, h,
                    marker: mRaster.data, hoechst: hRaster ? hRaster.data : null});
          const st = getChannelStats(mRaster.data as any);
          const lim = st.contrastLimits || [st.domain?.[0] ?? 0, st.domain?.[1] ?? 5000];
          los.push(lim[0]); his.push(lim[1]);
          dataMax = Math.max(dataMax, st.domain?.[1] ?? 5000);
        } catch (e: any) {
          console.warn(`[calib] ${sid} preview failed:`, e?.message);
        }
      }
      if (cancelled) return;
      setPreviews(out);
      // Seed the shared window from the pooled median across samples so the
      // reviewer starts near-right and mostly just confirms.
      const median = (arr: number[]) => {
        if (!arr.length) return 0;
        const a = arr.slice().sort((x, y) => x - y);
        return a[Math.floor(a.length / 2)];
      };
      if (los.length) {
        setLo(Math.max(0, Math.round(median(los))));
        setHi(Math.max(1, Math.round(median(his))));
      }
      if (seededColor) setColor(seededColor);
      setSliderMax(Math.max(8000, Math.round(dataMax)));
      setStatus(out.length ? "" : "No previews could be loaded for this marker.");
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [chosen, marker]);

  // Repaint every panel whenever the shared window/color/gamma changes.
  const repaint = useCallback(() => {
    const [cr, cg, cb] = hexToRgb(color);
    const span = Math.max(1, hi - lo);
    previews.forEach((p, i) => {
      const cv = canvasRefs.current[i];
      if (!cv) return;
      cv.width = p.w; cv.height = p.h;
      const ctx = cv.getContext("2d");
      if (!ctx) return;
      const img = ctx.createImageData(p.w, p.h);
      const d = img.data;
      const n = p.w * p.h;
      for (let k = 0; k < n; k++) {
        // Linear map — identical to the picker + export render. No gamma, so
        // what the reviewer approves here is exactly what a pick will show.
        let t = (p.marker[k] - lo) / span;
        t = t < 0 ? 0 : t > 1 ? 1 : t;
        let g = 0;
        if (p.hoechst) {
          const hv = clip01((p.hoechst[k] - HOECHST_LO) / (HOECHST_HI - HOECHST_LO));
          g = hv * HOECHST_DIM * 255;
        }
        const o = k * 4;
        d[o]     = Math.min(255, g + cr * t);
        d[o + 1] = Math.min(255, g + cg * t);
        d[o + 2] = Math.min(255, g + cb * t);
        d[o + 3] = 255;
      }
      ctx.putImageData(img, 0, 0);
    });
  }, [previews, lo, hi, color]);

  useEffect(() => { repaint(); }, [repaint]);

  async function approve() {
    setBusy(true);
    setStatus("Saving lab-canonical window…");
    try {
      await saveWindow(marker, {lo, hi, color}, reviewerName);
      onApproved({lo, hi, color, updated_by: reviewerName,
                  updated_at: new Date().toISOString()});
    } catch (e: any) {
      setStatus("Save failed: " + (e?.message || e));
      setBusy(false);
    }
  }

  const canApprove = confirmed && !busy && previews.length > 0 && hi > lo;

  return (
    <div className="calib-gate">
      <div className="calib-head">
        <div>
          <h2>Set the lab-canonical window for <span className="calib-marker">{marker}</span></h2>
          <p className="calib-sub">
            No shared window exists for {marker} yet. Tune one lo/hi/color that
            reads well on all {previews.length || N_SAMPLES} samples below, then
            approve. This becomes the standard everyone picks on — you only do
            this once per marker.
          </p>
        </div>
        <button className="btn ghost sm" disabled={loading || busy}
                onClick={() => setSeed(s => s + 1)}>↻ Shuffle samples</button>
      </div>

      {loading ? (
        <div className="calib-loading">{status || "Loading previews…"}</div>
      ) : (
        <>
          <div className="calib-grid">
            {previews.map((p, i) => (
              <div className="calib-panel" key={p.sid}>
                <canvas ref={el => { canvasRefs.current[i] = el; }} />
                <div className="calib-cap">
                  <span className={`calib-cond ${p.condition}`}>{p.condition}</span>
                  <span className="calib-sid">{p.sid}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="calib-controls">
            <label className="calib-ctl">
              <span>Min (lo)</span>
              <input type="range" min={0} max={sliderMax} value={lo}
                     onChange={e => setLo(Math.min(+e.target.value, hi - 1))} />
              <input type="number" min={0} max={sliderMax} value={lo}
                     onChange={e => setLo(Math.min(+e.target.value, hi - 1))} />
            </label>
            <label className="calib-ctl">
              <span>Max (hi)</span>
              <input type="range" min={1} max={sliderMax} value={hi}
                     onChange={e => setHi(Math.max(+e.target.value, lo + 1))} />
              <input type="number" min={1} max={sliderMax} value={hi}
                     onChange={e => setHi(Math.max(+e.target.value, lo + 1))} />
            </label>
            <label className="calib-ctl calib-color">
              <span>Color</span>
              <input type="color" value={color}
                     onChange={e => setColor(e.target.value)} />
            </label>
          </div>

          <div className="calib-approve">
            <label className="calib-confirm">
              <input type="checkbox" checked={confirmed}
                     onChange={e => setConfirmed(e.target.checked)} />
              This window reads well on all {previews.length} samples above.
            </label>
            <button className="btn primary" disabled={!canApprove} onClick={approve}>
              {busy ? "Saving…" : `Approve ${marker} canonical`}
            </button>
          </div>
          {status && <div className="calib-status">{status}</div>}
        </>
      )}
    </div>
  );
}
